import React from 'react';
import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import i18n from 'src/i18n';

import CourseService from 'src/services/course';
import CourseError from 'src/services/error/models/course';
import { LIST_LIMIT_PER_CALL } from 'src/constants';

import QuillViewModel from 'src/components/Quill/viewModel';
import RatingModalViewModel from './components/RatingModal/viewModel';
import RatingCardViewModel from './components/RatingCard/viewModel';

export default class InfoSectionViewModel {
  @observable status = {
    isReady: false
  }
  @observable selectedSort = {
    id: 'Newest',
    name: i18n.t('rating_latest_rank')
  };

  @observable sortList = [
    {
      id: 'Newest',
      name: i18n.t('rating_latest_rank')
    },
    {
      id: 'DESC',
      name: i18n.t('rating_highest_rank')
    },
    {
      id: 'ASC',
      name: i18n.t('rating_lowest_rank')
    }
  ]
  @observable isSelect = false;
  filterButtonRef = React.createRef();

  @observable description = null;
  @observable isOpen = false;

  @observable saleType;
  @observable isOwned;
  @observable author = {};
  @observable narrator = null;
  @observable vendor = null;
  @observable notice = {};
  @observable ratingInfo = {
    score: 0,
    statistics: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0
    }
  };
  @observable ratings = [];
  @observable myRating = {}

  @observable isMore = true;
  @observable anchor = null;
  @observable ratingModalVm = new RatingModalViewModel();
  @observable quillViewModel = new QuillViewModel();

  @observable quillRecorder = null;

  constructor() {
    makeObservable(this);
  }

  @action
  updateData = (data) => {
    const {
      id,
      introduction,
      author,
      narrator,
      vendor,
      notice,
      rating,
      saleType,
      isOwned,
      myRating
    } = data;

    this.id = id;
    this.description = introduction;
    this.author = author;
    this.narrator = narrator;
    this.vendor = vendor;
    this.notice = notice;
    this.ratingInfo = rating;
    this.saleType = saleType;
    this.isOwned = isOwned;
    this.myRating = myRating;

    this.ratingModalVm.updateData({ ...this.myRating, cid: id });
  }

  @action
  didMount = async (data) => {
    try {
      this.updateData(data);
      await this.getRatings(this.id);
      runInAction(() => {
        this.status.isReady = true;
        if (this.quillViewModel.quill) {
          this.quillViewModel.setQuillContent(this.description);
        } else {
          this.quillRecorder = setInterval(() => {
            if (this.quillViewModel.quill) {
              this.quillViewModel.setQuillContent(this.description);
              clearInterval(this.quillRecorder);
            }
          }, 1000);
        }
      });
    } catch (error) {
      console.error(error);
    }
  }

  @action
  getRatings = async () => {
    try {
      const { anchor, list } = await CourseService.getCourseRating(this.id, this.anchor, this.selectedSort.id);
      runInAction(() => {
        this.anchor = anchor;
        if (list.length < LIST_LIMIT_PER_CALL.commentAndRating) {
          this.isMore = false;
        }
        this.ratings.push(...list.map((el) => new RatingCardViewModel(el)));
      });
    } catch (error) {
      CourseError.getCourseRating(error);
    }
  }

  @action
  changeIsOpen = () => {
    this.isOpen = !this.isOpen;
  }

  @action
  onMoreClick = async () => {
    try {
      await this.getRatings();
    } catch (error) {
      console.error(error);
    }
  }

  @action
  onEnter = async () => {
    try {
      if (!this.status.isReady) {
        return;
      }
      await this.getRatings();
    } catch (error) {
      console.error(error);
    }
  }

  @action
  handleClick = () => {
    if (!this.isSelect) {
      this.onSelectOpen();
    } else {
      this.onSelectClose();
    }
  }

  @action
  onSelectOpen = () => {
    this.isSelect = true;
    this.filterButtonRef.current.focus();
  }

  @action
  onSelectClose = () => {
    this.isSelect = false;
  }

  @action
  handleSelect = async (method) => {
    try {
      this.ratings = [];
      this.anchor = null;
      this.selectedSort = method;
      await this.getRatings();
    } catch (error) {
      console.error(error);
    }
  }

  @action
  handleModalConfirm = async (refreshFunction) => {
    try {
      this.ratings = [];
      this.anchor = null;
      this.selectedSort = {
        id: 'Newest',
        name: i18n.t('rating_latest_rank')
      };
      await this.getRatings();
      await refreshFunction();
    } catch (error) {
      console.error(error);
    }
  }

  @computed
  get totalRatingCount() {
    const totalCount = this.ratingInfo.statistics[1] + this.ratingInfo.statistics[2] + this.ratingInfo.statistics[3] + this.ratingInfo.statistics[4] + this.ratingInfo.statistics[5];
    const afterDeco = totalCount;
    return afterDeco;
  }

  @computed
  get totalScoreMathRound() {
    return Math.round(this.ratingInfo.score * 10) / 10;
  }

  @computed
  get scoreText() {
    return this.totalScoreMathRound.toString().length < 2 ? `${this.totalScoreMathRound}.0` : this.totalScoreMathRound;
  }
}
